import { Alert, Flex, Skeleton, theme } from "antd"
import React, { Suspense, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { FieldLayer, LazyPdfPages } from "../../../components/Esign/index.ts"
import { fetchOwnFields } from "../api/signing.services"
import type { OwnField, OwnFields } from "../types/signing.types"
import PdfViewer from "./PdfViewer"

interface Props {
  session: string
  documentUrl: string
  documentBlob: Blob | null
  recipientName: string
  isSigner: boolean
  height: string
}

type MarkedDocument = { bytes: Uint8Array; placement: OwnFields }

const bytesOf = async (blob: Blob): Promise<Uint8Array> => {
  if (typeof blob.arrayBuffer === "function") {
    return new Uint8Array(await blob.arrayBuffer())
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob)
  })
}

/** Falls back to the browser's own viewer, unmarked, when anything stops the drawing. */
const SigningDocumentView: React.FC<Props> = ({
  session,
  documentUrl,
  documentBlob,
  recipientName,
  isSigner,
  height
}) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [marked, setMarked] = useState<MarkedDocument | null>(null)
  const [fallback, setFallback] = useState(!documentBlob)

  useEffect(() => {
    if (!documentBlob) {
      setFallback(true)
      return
    }
    let abandoned = false
    setMarked(null)
    setFallback(false)
    Promise.all([bytesOf(documentBlob), fetchOwnFields(session)])
      .then(([bytes, response]) => {
        if (abandoned) return
        if (!response.ok || !response.data) {
          setFallback(true)
          return
        }
        setMarked({ bytes, placement: response.data })
      })
      .catch(() => {
        if (!abandoned) setFallback(true)
      })
    return () => {
      abandoned = true
    }
  }, [documentBlob, session])

  const comesLater = isSigner && (
    <Alert
      type="info"
      showIcon
      message={t("signing.document.signingComesLater")}
    />
  )

  if (fallback) {
    return (
      <Flex vertical gap={16}>
        <PdfViewer url={documentUrl} height={height} />
        {comesLater}
      </Flex>
    )
  }

  if (!marked) {
    return (
      <div aria-busy aria-label={t("esign.pages.loading")}>
        <Skeleton.Node active className="!w-full" style={{ height }} />
      </div>
    )
  }

  const fields: OwnField[] = isSigner ? marked.placement.fields : []

  return (
    <Flex vertical gap={16}>
      <div
        className="overflow-auto rounded-lg p-2"
        style={{
          maxHeight: height,
          background: token.colorFillTertiary,
          border: `1px solid ${token.colorBorderSecondary}`
        }}
      >
        <Suspense fallback={<Skeleton active paragraph={{ rows: 8 }} />}>
          <LazyPdfPages
            data={marked.bytes}
            pages={marked.placement.pages}
            onError={() => setFallback(true)}
            renderOverlay={(pageNumber, pageCount) => (
              <FieldLayer
                items={fields.filter((field) => field.page === pageNumber)}
                render={(field, style) => (
                  <button
                    type="button"
                    aria-disabled
                    aria-label={t("signing.document.fieldLabel", {
                      type: t(`esign.fieldTypes.${field.field_type}`),
                      name: recipientName,
                      page: pageNumber,
                      count: pageCount
                    })}
                    onClick={(event) => event.preventDefault()}
                    className="flex cursor-default items-center overflow-hidden rounded-sm px-1 text-left text-xs font-semibold"
                    style={{
                      ...style,
                      border: `2px dashed ${token.colorPrimary}`,
                      background: token.colorPrimaryBg,
                      color: token.colorPrimaryText
                    }}
                  >
                    <Flex vertical gap={0} className="min-w-0">
                      <span className="truncate">
                        {t("signing.document.signHere")}
                      </span>
                      <span className="truncate font-normal">
                        {field.label ||
                          t(`esign.fieldTypes.${field.field_type}`)}
                      </span>
                    </Flex>
                  </button>
                )}
              />
            )}
          />
        </Suspense>
      </div>
      {fields.length > 0 ? (
        <Alert
          type="info"
          showIcon
          message={t("signing.document.fieldsHighlighted")}
        />
      ) : (
        comesLater
      )}
    </Flex>
  )
}

export default SigningDocumentView
